<script setup lang="ts">
//#region imports
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useApi } from '@/api';
import eventBus from '@/setup/eventBus';
import type { EmitsToListeners } from '@/types/core';
//#endregion

//#region types
interface  Emits {
  'success': [],
  'close': [],
}

export interface CreateDedicatedSiteDomainsParams {
  listeners?: EmitsToListeners<Emits>
}
//#endregion

//#region define
const emit = defineEmits<Emits>();
//#endregion

//#region composables
const api = useApi();
const { t } = useI18n();
//#endregion

//#region state
const isVisible = ref(false);
const isLoading = ref(false);
const error = ref(false);

const domainList = ref('');
//#endregion

//#region computed
const isSaveDisabled = computed(() => {
  return !domainList.value || error.value;
});
//#endregion

//#region methods
function open() {
  isVisible.value = true;
}

function handleClose() {
  emit('close');
  isVisible.value = false;
}

async function handleSave() {
  const failedDomains: string[] = [];
  const domains: string[] = domainList.value.split('\n').map((item) => item.trim()).filter(String);

  const worker = async () => {
    while (domains.length > 0) {
      const domain = domains.shift();
      if (!domain) continue;

      try {
        await api.createPlatformBettingDomain({
          domain_name: domain,
          affiliate_offer_id: null,
        });
      } catch (error) {
        failedDomains.push(domain);
      }
    }
  };

  await Promise.all([worker(), worker(), worker()]);

  if (failedDomains.length === 0) {
    isVisible.value = false;
  } else {
    domainList.value = failedDomains.join('\n');
    error.value = true;
  }
  emit('success');
}

function onCopy() {
  eventBus.$emit('notify-event', {
    type: 'info',
    text: t('domainManagement.messages.copied_to_clipboard'),
  });
}

function onError() {
  eventBus.$emit('notify-event', {
    type: 'error',
    text: t('domainManagement.messages.copy_error'),
  });
}
//#endregion

defineExpose({
  open,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="create-dedicated-site-domains-popup"
    :title="$t('domainManagement.popup.add_dedicated_site_domain')"
    :is-loading="isLoading"
    :width="480"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <div class="create-dedicated-site-domains-popup__message">
          <UiIcon
            v-if="error"
            :size="24"
            color="var(--text-negative)"
            name="exclamation-triangle"
            class="t:mr-12"
          />
          {{ error ? $t('domainManagement.popup.text_error') : $t('domainManagement.popup.info_input') }}
        </div>
        <UiTextarea
          v-model="domainList"
          :label="$t('domainManagement.popup.domain_names')"
          :placeholder="$t(`reports.other.search_by.placeholder.referrer_domain`)"
          :rows="6"
          is-clear
          :is-error="error"
          @focus="error = false"
        />
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:flex t:justify-between">
        <div>
          <UiButton
            v-if="error"
            v-clipboard="domainList"
            v-clipboard:success="onCopy"
            v-clipboard:error="onError"
            icon="copy"
          >
            {{ $t('team_popup.copy_button') }}
          </UiButton>
        </div>
        <div class="t:space-x-8">
          <UiButton
            type="negative"
            size="big"
            icon="times"
            :disabled="isLoading"
            @click="handleClose"
          >
            {{ $t(`domainManagement.popup.cancel`) }}
          </UiButton>
          <UiButton
            filled
            size="big"
            icon="check"
            :disabled="isSaveDisabled || isLoading"
            @click="handleSave"
          >
            {{ $t('reports.other.add_domains') }}
          </UiButton>
        </div>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.create-dedicated-site-domains-popup {
  &__message {
    display: flex;
    align-items: center;
    color: var(--text-body);
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
  }
}
</style>
